import { questionsLeft, ROLE_LABELS } from '../lib/auth';
import { useAuth } from './AuthProvider';
import { ChevronRight, InitialsAvatar } from './brand';
import { ModalShell } from './Modal';

function ProfileRow({ label, value, onClick, last }: { label: string; value: string; onClick?: () => void; last?: boolean }) {
  return (
    <button
      onClick={onClick}
      className="he-press"
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 16px',
        border: 'none',
        borderBottom: last ? 'none' : '1px solid var(--border-1)',
        background: 'transparent',
        cursor: onClick ? 'pointer' : 'default',
        fontFamily: 'inherit',
        textAlign: 'left',
      }}
    >
      <span style={{ fontSize: 14, color: 'var(--fg-2)', flex: 1 }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 600 }}>{value}</span>
      <ChevronRight />
    </button>
  );
}

export function Profile({ onClose, onSignOut }: { onClose: () => void; onSignOut: () => void }) {
  const { user } = useAuth();
  if (!user) return null;

  const unlimited = user.resetDays === 0;
  const lim = user.questionLimit;
  const left = questionsLeft(user);
  const pctLeft = lim > 0 ? (left / lim) * 100 : 100;
  const r = 26;
  const c = 2 * Math.PI * r;
  const off = c * (1 - pctLeft / 100);
  const roleLabel = ROLE_LABELS[user.role];

  return (
    <ModalShell onClose={onClose} maxWidth={460}>
      {/* header */}
      <div style={{ padding: '26px 24px 22px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ position: 'relative', width: 60, height: 60, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="60" height="60" viewBox="0 0 60 60" style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}>
            <circle cx="30" cy="30" r={r} fill="none" stroke="var(--bg-3)" strokeWidth="4" />
            <circle cx="30" cy="30" r={r} fill="none" stroke="var(--he-purple)" strokeWidth="4" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} />
          </svg>
          <InitialsAvatar initials={user.initials} size={46} fontSize={17} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em' }}>{user.name}</div>
          <div style={{ fontSize: 14, color: 'var(--fg-2)' }}>{user.email}</div>
          <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>{user.org}</div>
        </div>
      </div>

      {/* usage / credits */}
      <div style={{ margin: '0 16px 16px', borderRadius: 16, padding: 16, background: 'var(--bg-2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '.05em',
              textTransform: 'uppercase',
              color: 'var(--he-purple)',
              background: 'rgba(95,38,252,.08)',
              padding: '3px 9px',
              borderRadius: 999,
            }}
          >
            {user.plan}
          </span>
          <span style={{ fontSize: 12.5, color: 'var(--fg-2)' }}>
            {unlimited ? 'No monthly limit' : `Resets in ${user.resetDays} days · ${user.resetLabel}`}
          </span>
        </div>
        {unlimited ? (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
            <span style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1 }}>Unlimited</span>
            <span style={{ fontSize: 14, color: 'var(--fg-2)' }}>questions</span>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginBottom: 9 }}>
              <span style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1 }}>{left}</span>
              <span style={{ fontSize: 14, color: 'var(--fg-2)' }}>of {lim} questions left this month</span>
            </div>
            <div style={{ height: 8, borderRadius: 999, background: 'var(--bg-3)', overflow: 'hidden' }}>
              <div style={{ width: pctLeft + '%', height: '100%', background: 'var(--he-purple)', borderRadius: 999 }} />
            </div>
          </>
        )}
        <div style={{ display: 'flex', gap: 9, marginTop: 14 }}>
          <button
            className="he-press"
            style={{ flex: 1, padding: '11px 0', borderRadius: 11, border: 'none', background: 'var(--he-purple)', color: '#fff', fontWeight: 600, fontSize: 14, fontFamily: 'inherit', cursor: 'pointer' }}
          >
            Upgrade plan
          </button>
          <button
            className="he-press"
            style={{ flex: 1, padding: '11px 0', borderRadius: 11, border: '1px solid var(--border-1)', background: 'var(--bg-1)', color: 'var(--fg-1)', fontWeight: 600, fontSize: 14, fontFamily: 'inherit', cursor: 'pointer' }}
          >
            Usage details
          </button>
        </div>
      </div>

      {/* account settings */}
      <div style={{ margin: '0 16px 8px', borderRadius: 16, overflow: 'hidden' }}>
        <ProfileRow label="Role" value={roleLabel} />
        <ProfileRow label="Organization" value={user.org} last />
      </div>

      <div style={{ padding: '8px 16px 22px' }}>
        <button
          onClick={onSignOut}
          className="he-press"
          style={{
            width: '100%',
            padding: '12px 0',
            borderRadius: 12,
            border: '1px solid var(--border-1)',
            background: 'var(--bg-1)',
            color: '#C2410C',
            fontWeight: 600,
            fontSize: 14,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Sign out
        </button>
      </div>
    </ModalShell>
  );
}
